import { connect, ConnectedProps } from 'react-redux'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import { DisplayPriceGettingSpecificPriceParams } from '../../../../../baseComponents/Displays/DisplayPrice'
import { permissionCheck } from '../../../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import BaseScreenTableInner from '../../../../../baseComponents/TwinTable/BaseScreenTableInner'
import { TableComponentBaseScreenInnerProps } from '../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderBoolean, DeleteRow } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import withLoading from '../../../../../utils/hoc/withLoading'
import useProductActiveDictionary from '../../../../../utils/hooks/product/useProductActiveDictionary'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { SpecificPriceRulesPayload } from '../../../../../utils/reducers/products/specificPriceRules'
import ModalCEProductComposed from './ModalCEProductComposed'
import ModalDeleteProductComposed from './ModalDeleteProductComposed'
import { useMemo } from 'react'
import { ProductExtendedTyLong, ProductExtendedTyShort } from '../../../types'
import locations from '../../../../../utils/globals/locations'
import { RectangularInputFormatNumberSmall } from '../../../../../forms/Input'


interface ProductComposedProps extends ComponentWithPermissions {
    basicData: ProductExtendedTyShort
}

const ProductComposed: React.FC<ProductComposedProps> = ({ basicData, userPermissions }) => {
    const { t } = useTwinTranslation()
    const pageData = {
        title: t('productComposed', 'Producto Compuesto'),
        newText: t('add', 'Añadir'),
    }

    return (
        <BaseScreenTableInner TableComponent={TableConnect} CreateModalComponent={ModalCEProductComposed} DeleteModalComponent={ModalDeleteProductComposed} pageData={pageData} userPermissions={userPermissions} extraWhereParams={{
            'ProductComposed.ProductId': basicData?.id
        }} />
    )
}
type TableProductComposedProps = TableComponentBaseScreenInnerProps

const TableProductComposed: React.FC<TableProductComposedProps & ReduxSpecificPricesTable> = ({ setOpened, setTableInstance, extraWhereParams, userPermissions, specificPriceRules }) => {
    const { t } = useTwinTranslation()
    const { productActiveDict } = useProductActiveDictionary()

    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [specificPriceRules?.arrSorted])
    
    const columns: ColumnTableSchema<ProductExtendedTyLong> = [
        {
            id: 'ProductsComposedId',
            dataKey: 'ProductsComposedId',
            label: t('id', 'ID'),
            width: 50,
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'units',
            dataKey: 'units',
            label: t('units', 'Unidades'),
            width: 75,
            customRender: (parameterValue) => <RectangularInputFormatNumberSmall value={parameterValue ?? 1} readOnly={true} className='w-60' />,
        },
        {
            id: 'price',
            dataKey: 'SpecificPriceRuleId',
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || defaultSpecificPriceRuleId)
                return <span>{t('price', 'Precio') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            minWidth: 150,
            simpleLabel: t('price', 'Precio'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            generalSearchable: false,
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPriceGettingSpecificPriceParams value={allRowData?.calculated_price} CurrencyId={specificPriceRules?.dictionary[allRowData.SpecificPriceRuleId]?.CurrencyId || ''} with_vat={false} specificPriceRules={specificPriceRules} SpecificPriceRuleId={defaultSpecificPriceRuleId} />
            }
        },
        {
            id: 'active',
            dataKey: 'ProductsComposed.active' as any,
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: productActiveDict, label: t('active', 'Activo') }
            },
            generalSearchable: false
        },
        {
            id: 'delete',
            dataKey: 'ProductsComposedId',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        },
    ]
    return (
        <TwinTableLink columns={columns} name='subProductComposed' createRowDataLink={[locations.product, ['$ProductsComposedId', 'main']]} getDataFrom='/api/app/product/productComposed/getAllProductsComposedComplete'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })} setTableInstance={setTableInstance} defaultOrder={{ 'defaultOrderField': 'ProductId' }} extraWhereParams={{ ...extraWhereParams }} extraWhereSubstituibleParams={{ 'SpecificPriceRuleId': defaultSpecificPriceRuleId }} />
    )
}

const productComposedTableDispatch = {
    setSpecificPriceRules: (payload: SpecificPriceRulesPayload) => ({ type: 'CHANGE_SPECIFICPRICERULES', payload }),
}

type ReduxSpecificPricesTable = ConnectedProps<typeof productComposedTableConnect>
const mapProductComposedTableConnector = (state: AllReduxPayloads) => ({ specificPriceRules: state.specificPriceRules })
const productComposedTableConnect = connect(mapProductComposedTableConnector, productComposedTableDispatch)

const productComposedTableConnectLoading = withLoading(TableProductComposed, [{ fetchUrl: '/api/app/product/specificPrice/getAllSpecificPriceRules', propName: 'specificPriceRules', setFunctionName: 'setSpecificPriceRules' }])

const TableConnect = productComposedTableConnect(productComposedTableConnectLoading)

export default ProductComposed